<template>
  <div class="report-view-container">
    <el-row class="header_zone mt-10">
      <el-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
        <h2 class="report-title">{{ reportName || 'Тайлан' }}</h2>
      </el-col>
      <el-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12" class="header-actions-col">
        <div class="header-actions">
          <el-button type="primary" @click="executeReport" :loading="loading" size="small" class="action-btn">Ажиллуулах</el-button>
          <el-button type="success" @click="exportToExcel" :disabled="!tableData || tableData.length === 0" size="small" class="action-btn">Excel</el-button>
          <el-button type="warning" @click="exportToPDF" :disabled="!tableData || tableData.length === 0" size="small" class="action-btn">PDF</el-button>
          <el-button type="info" @click="exportToCSV" :disabled="!tableData || tableData.length === 0" size="small" class="action-btn">CSV</el-button>
          <el-button @click="printReport" :disabled="!tableData || tableData.length === 0" size="small" class="action-btn">Хэвлэх</el-button>
        </div>
      </el-col>
    </el-row>

    <!-- Хугацаа сонгох хэсэг -->
    <el-row class="mt-10">
      <el-col>
        <el-card>
          <template #header>
            <div class="date-range-header">
              <span>Хугацаа сонгох</span>
              <el-button
                size="small"
                @click="showQueryPreview = !showQueryPreview"
                type="info"
              >
                <font-awesome-icon icon="code" />
                Query харах
              </el-button>
            </div>
          </template>
          <!-- Query Preview -->
          <el-collapse-transition>
            <div v-show="showQueryPreview" class="query-preview">
              <el-alert
                :title="`Тайлан: ${reportName}`"
                type="info"
                :closable="false"
                style="margin-bottom: 12px;"
              />
              <pre class="query-code">{{ currentQuery }}</pre>
            </div>
          </el-collapse-transition>
          <el-form :inline="true" :model="dateRange" class="demo-form-inline">
            <el-form-item label="Эхлэх огноо">
              <el-date-picker
                v-model="dateRange.startDate"
                type="date"
                placeholder="Эхлэх огноо сонгох"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                style="width: 200px"
              />
            </el-form-item>
            <el-form-item label="Дуусах огноо">
              <el-date-picker
                v-model="dateRange.endDate"
                type="date"
                placeholder="Дуусах огноо сонгох"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                style="width: 200px"
              />
            </el-form-item>
            <el-form-item label="Салбар">
              <el-select
                v-model="filters.branch_id"
                placeholder="Бүх салбар"
                clearable
                style="width: 200px"
                :loading="branches.length === 0"
              >
                <el-option
                  v-for="branch in branches"
                  :key="branch.id"
                  :label="branch.branch_name"
                  :value="branch.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button-group>
                <el-button @click="setDateRange('today')">Өнөөдөр</el-button>
                <el-button @click="setDateRange('week')">Энэ долоо хоног</el-button>
                <el-button @click="setDateRange('month')">Энэ сар</el-button>
                <el-button @click="setDateRange('year')">Энэ жил</el-button>
                <el-button @click="setDefaultDateRange">Өнөөдрөөс сар</el-button>
              </el-button-group>
            </el-form-item>
          </el-form>
        </el-card>
      </el-col>
    </el-row>

    <!-- KPI Cards -->
    <ReportKPICards v-if="tableData && tableData.length > 0" :data="tableData" :columns="tableColumns" />

    <!-- Тайлангийн үр дүн -->
    <el-row class="mt-10" v-if="tableData && tableData.length > 0">
      <el-col>
        <el-card>
          <template #header>
            <div class="table-header-actions">
              <span>Тайлангийн үр дүн</span>
              <div class="header-actions-group">
                <el-button-group>
                  <el-button size="small" @click="viewMode = 'table'" :type="viewMode === 'table' ? 'primary' : ''">Хүснэгт</el-button>
                  <el-button size="small" @click="viewMode = 'chart'" :type="viewMode === 'chart' ? 'primary' : ''">График</el-button>
                </el-button-group>
                <el-button
                  v-if="viewMode === 'table'"
                  size="small"
                  @click="showColumnSelector = true"
                  type="info"
                >
                  <font-awesome-icon icon="eye" />
                  Багана
                </el-button>
                <el-button
                  size="small"
                  @click="executeReport"
                  :loading="loading"
                  type="primary"
                >
                  <font-awesome-icon icon="arrows-rotate" />
                  Шинэчлэх
                </el-button>
              </div>
            </div>
          </template>
          <!-- Table Filters -->
          <div v-if="viewMode === 'table'" class="table-filters">
            <el-input
              v-model="tableSearchQuery"
              placeholder="Хайх..."
              class="table-search-input"
              clearable
            >
              <template #prefix>
                <font-awesome-icon icon="search" />
              </template>
            </el-input>
          </div>
          <el-table
            v-if="viewMode === 'table'"
            :data="paginatedTableData"
            style="width: 100%"
            border
            stripe
            max-height="600"
            @sort-change="handleTableSortChange"
          >
            <el-table-column
              v-for="(column, index) in visibleColumns"
              :key="index"
              :prop="column"
              :label="formatColumnName(column)"
              :min-width="150"
              sortable="custom"
              :filters="getColumnFilters(column)"
              :filter-method="(value, row) => filterColumn(column, value, row)"
              filter-placement="bottom-end"
            >
            </el-table-column>
          </el-table>

          <!-- Chart View -->
          <div v-if="viewMode === 'chart'" class="chart-view-container">
            <el-row :gutter="16" class="chart-controls">
              <el-col :xs="24" :sm="12" :md="8" :lg="6" :xl="6">
                <el-form-item label="Графикийн төрөл">
                  <el-select v-model="chartType" placeholder="Сонгох" style="width: 100%">
                    <el-option label="Багана" value="bar" />
                    <el-option label="Шугам" value="line" />
                    <el-option label="Тойрог" value="pie" />
                    <el-option label="Талбай" value="area" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12" :md="8" :lg="6" :xl="6">
                <el-form-item label="X тэнхлэг">
                  <el-select v-model="chartXColumn" placeholder="Сонгох" style="width: 100%">
                    <el-option v-for="col in tableColumns" :key="col" :label="formatColumnName(col)" :value="col" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12" :md="8" :lg="6" :xl="6">
                <el-form-item label="Y тэнхлэг">
                  <el-select v-model="chartYColumn" placeholder="Сонгох" style="width: 100%">
                    <el-option v-for="col in numericColumns" :key="col" :label="formatColumnName(col)" :value="col" />
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>
            <div class="chart-wrapper">
              <ReportChart
                v-if="chartXColumn && chartYColumn"
                :data="tableData"
                :x-column="chartXColumn"
                :y-column="chartYColumn"
                :chart-type="chartType"
              />
            </div>
          </div>

          <!-- Pagination -->
          <el-pagination
            v-if="viewMode === 'table' && filteredTableData.length > 0"
            @current-change="handlePageChange"
            :current-page="currentPage"
            :page-size="pageSize"
            :total="filteredTableData.length"
            layout="total, prev, pager, next, jumper"
            style="margin-top: 20px; text-align: right;"
          />
        </el-card>
      </el-col>
    </el-row>

    <!-- Loading State -->
    <el-row class="mt-10" v-if="loading">
      <el-col>
        <el-card>
          <div class="loading-container">
            <el-icon class="is-loading" :size="40">
              <font-awesome-icon icon="spinner" />
            </el-icon>
            <p class="loading-text">Тайлан ажиллуулж байна...</p>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Хоосон мэдээлэл -->
    <el-row class="mt-10" v-if="!loading && (!tableData || tableData.length === 0)">
      <el-col>
        <el-card>
          <el-empty>
            <template #description>
              <div class="empty-state">
                <p class="empty-title">Тайлангийн үр дүн байхгүй байна</p>
                <p class="empty-subtitle">Хугацаа сонгож тайлан ажиллуулна уу</p>
                <el-button type="primary" @click="executeReport" class="empty-action-button">
                  <font-awesome-icon icon="play" />
                  Тайлан ажиллуулах
                </el-button>
              </div>
            </template>
          </el-empty>
        </el-card>
      </el-col>
    </el-row>

    <!-- Column Selector Dialog -->
    <el-dialog
      v-model="showColumnSelector"
      title="Багана сонгох"
      width="400px"
    >
      <el-checkbox-group v-model="hiddenColumns">
        <el-checkbox
          v-for="column in tableColumns"
          :key="column"
          :label="column"
          :value="column"
        >
          {{ formatColumnName(column) }}
        </el-checkbox>
      </el-checkbox-group>
      <template #footer>
        <el-button @click="showColumnSelector = false">Хаах</el-button>
        <el-button type="primary" @click="showColumnSelector = false">Хадгалах</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import services from '@/services'
import { ElMessage } from 'element-plus'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import dayjs from 'dayjs'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import ReportChart from '@/components/ReportChart.vue'
import ReportKPICards from '@/components/ReportKPICards.vue'

export default {
  name: 'ReportView',
  components: {
    ReportChart,
    ReportKPICards
  },
  data () {
    return {
      reportId: null,
      reportName: '',
      dateRange: {
        startDate: '',
        endDate: ''
      },
      filters: {
        branch_id: null
      },
      branches: [],
      tableData: [],
      tableColumns: [],
      loading: false,
      currentPage: 1,
      pageSize: 20,
      viewMode: 'table',
      chartType: 'bar',
      chartXColumn: '',
      chartYColumn: '',
      tableSearchQuery: '',
      showColumnSelector: false,
      hiddenColumns: [],
      tableSortColumn: null,
      tableSortOrder: null,
      showQueryPreview: false,
      currentQuery: '',
      columnFilters: {}
    }
  },
  computed: {
    filteredData () {
      if (!this.tableData || this.tableData.length === 0) return []
      return [...this.tableData]
    },
    visibleColumns () {
      return this.tableColumns.filter(col => !this.hiddenColumns.includes(col))
    },
    filteredTableData () {
      let result = [...this.tableData]

      // Search filter
      if (this.tableSearchQuery) {
        const query = this.tableSearchQuery.toLowerCase()
        result = result.filter(row => {
          return this.visibleColumns.some(col => {
            const value = row[col]
            return value && String(value).toLowerCase().includes(query)
          })
        })
      }

      // Column filters
      Object.keys(this.columnFilters).forEach(column => {
        const filterValue = this.columnFilters[column]
        if (filterValue && filterValue.length > 0) {
          result = result.filter(row => {
            const cellValue = row[column]
            if (cellValue === null || cellValue === undefined) return false
            return filterValue.includes(String(cellValue))
          })
        }
      })

      // Sorting
      if (this.tableSortColumn && this.tableSortOrder) {
        result.sort((a, b) => {
          const aVal = a[this.tableSortColumn]
          const bVal = b[this.tableSortColumn]

          if (aVal === bVal) return 0
          if (aVal === null || aVal === undefined) return 1
          if (bVal === null || bVal === undefined) return -1

          const comparison = aVal > bVal ? 1 : -1
          return this.tableSortOrder === 'ascending' ? comparison : -comparison
        })
      }

      return result
    },
    uniqueColumnValues () {
      const values = {}
      this.visibleColumns.forEach(column => {
        const uniqueValues = [...new Set(this.tableData.map(row => row[column]).filter(v => v != null && v !== ''))]
        values[column] = uniqueValues.sort()
      })
      return values
    },
    paginatedTableData () {
      const startIndex = (this.currentPage - 1) * this.pageSize
      return this.filteredTableData.slice(startIndex, startIndex + this.pageSize)
    },
    numericColumns () {
      if (!this.tableData || this.tableData.length === 0) return []
      return this.tableColumns.filter(col => {
        const sample = this.tableData[0]?.[col]
        return typeof sample === 'number' || !isNaN(parseFloat(sample))
      })
    }
  },
  created () {
    this.initializeReport()
    this.loadBranches()
  },
  watch: {
    '$route' (to, from) {
      // Route өөрчлөгдөхөд тайлангийн мэдээллийг дахин ачаалах
      if (to.params.id !== from.params.id) {
        this.initializeReport()
      }
    }
  },
  methods: {
    initializeReport () {
      this.reportId = this.$route.params.id
      this.tableData = []
      this.tableColumns = []
      this.loadReportInfo()
      this.setDefaultDateRange()
    },
    async loadReportInfo () {
      try {
        const response = await services.get(`reports/${this.reportId}`)
        if (response && response.type === 'success') {
          this.reportName = response.data.name
          this.updateQueryPreview()
        }
      } catch (error) {
        console.error('Error loading report info:', error)
        ElMessage.error('Тайлангийн мэдээлэл ачаалахад алдаа гарлаа')
      }
    },
    updateQueryPreview () {
      // Query preview-ийг шинэчлэх
      if (this.reportId) {
        // Энэ нь executeReport-ийн дараа шинэчлэгдэнэ
        this.currentQuery = 'Query ажиллуулах шаардлагатай...'
      }
    },
    async updateQueryPreviewAfterExecution () {
      try {
        const response = await services.get(`reports/${this.reportId}`)
        if (response && response.type === 'success') {
          let query = response.data.query
          if (this.dateRange.startDate && this.dateRange.endDate) {
            const escapedStartDate = `${this.dateRange.startDate} 00:00:00`
            const escapedEndDate = `${this.dateRange.endDate} 23:59:59`
            query = query.replace(/:startDate/g, `'${escapedStartDate}'`)
            query = query.replace(/:endDate/g, `'${escapedEndDate}'`)
          }
          if (this.filters.branch_id) {
            query = query.replace(/:branch_id/g, this.filters.branch_id)
          }
          this.currentQuery = query
        }
      } catch (error) {
        console.error('Error loading query:', error)
      }
    },
    setDefaultDateRange () {
      // Өнөөдрөөс сарын хугацаа
      const today = dayjs()
      const oneMonthAgo = today.subtract(1, 'month')

      this.dateRange.endDate = today.format('YYYY-MM-DD')
      this.dateRange.startDate = oneMonthAgo.format('YYYY-MM-DD')
    },
    setDateRange (type) {
      const today = dayjs()
      let startDate, endDate

      switch (type) {
        case 'today':
          startDate = today
          endDate = today
          break
        case 'week':
          startDate = today.startOf('week')
          endDate = today.endOf('week')
          break
        case 'month':
          startDate = today.startOf('month')
          endDate = today.endOf('month')
          break
        case 'year':
          startDate = today.startOf('year')
          endDate = today.endOf('year')
          break
      }

      this.dateRange.startDate = startDate.format('YYYY-MM-DD')
      this.dateRange.endDate = endDate.format('YYYY-MM-DD')
    },
    async executeReport () {
      if (!this.dateRange.startDate || !this.dateRange.endDate) {
        ElMessage.warning('Эхлэх болон дуусах огноо сонгоно уу')
        return
      }

      if (new Date(this.dateRange.startDate) > new Date(this.dateRange.endDate)) {
        ElMessage.warning('Эхлэх огноо нь дуусах огнооноос их байж болохгүй')
        return
      }

      this.loading = true
      try {
        const params = {
          startDate: this.dateRange.startDate,
          endDate: this.dateRange.endDate
        }

        // Filter параметрүүдийг нэмэх
        if (this.filters.branch_id) {
          params.branch_id = this.filters.branch_id
        }

        const response = await services.get(`reports/${this.reportId}/execute`, params)

        if (response && response.type === 'success') {
          this.tableData = response.data || []

          // Columns-ийг үр дүнгээс автоматаар үүсгэх
          if (this.tableData.length > 0) {
            this.tableColumns = Object.keys(this.tableData[0])
            // Chart columns автоматаар сонгох
            if (this.numericColumns.length > 0) {
              this.chartYColumn = this.numericColumns[0]
            }
            if (this.tableColumns.length > 0) {
              this.chartXColumn = this.tableColumns[0]
            }
          } else {
            this.tableColumns = []
          }

          // Query preview шинэчлэх
          this.updateQueryPreviewAfterExecution()

          ElMessage.success('Тайлан амжилттай ажиллууллаа')
        } else {
          const errorMsg = response?.error || response?.message || 'Тайлан ажиллуулахад алдаа гарлаа'
          ElMessage.error({
            message: errorMsg,
            duration: 5000,
            showClose: true
          })
          this.tableData = []
          this.tableColumns = []
        }
      } catch (error) {
        console.error('Error executing report:', error)
        let errorMessage = 'Тайлан ажиллуулахад алдаа гарлаа'

        if (error.response?.data?.error) {
          errorMessage = error.response.data.error
        } else if (error.message) {
          errorMessage = error.message
        }

        ElMessage.error({
          message: errorMessage,
          duration: 5000,
          showClose: true
        })
        this.tableData = []
        this.tableColumns = []
      } finally {
        this.loading = false
        this.currentPage = 1
      }
    },
    exportToExcel () {
      if (!this.tableData || this.tableData.length === 0) {
        ElMessage.warning('Татах өгөгдөл байхгүй байна')
        return
      }

      try {
        // Excel файл үүсгэх
        const worksheet = XLSX.utils.json_to_sheet(this.tableData)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Тайлан')

        // Файл татах
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' })
        const fileName = `${this.reportName || 'Тайлан'}_${this.dateRange.startDate}_${this.dateRange.endDate}.xlsx`
        saveAs(data, fileName)

        ElMessage.success('Excel файл амжилттай татлаа')
      } catch (error) {
        console.error('Error exporting to Excel:', error)
        ElMessage.error('Excel файл татахад алдаа гарлаа')
      }
    },
    formatColumnName (column) {
      // Column нэрийг илүү унших боломжтой болгох
      return column
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    handlePageChange (page) {
      this.currentPage = page
    },
    handleTableSortChange ({ column, prop, order }) {
      this.tableSortColumn = prop
      this.tableSortOrder = order
      this.currentPage = 1
    },
    exportToPDF () {
      if (!this.tableData || this.tableData.length === 0) {
        ElMessage.warning('Татах өгөгдөл байхгүй байна')
        return
      }

      try {
        // eslint-disable-next-line new-cap
        const doc = new jsPDF()
        doc.text(this.reportName || 'Тайлан', 14, 15)
        doc.text(`Хугацаа: ${this.dateRange.startDate} - ${this.dateRange.endDate}`, 14, 22)

        const columns = this.tableColumns.map(col => this.formatColumnName(col))
        const rows = this.tableData.map(row => this.tableColumns.map(col => row[col] || ''))

        autoTable(doc, {
          head: [columns],
          body: rows,
          startY: 30
        })

        const fileName = `${this.reportName || 'Тайлан'}_${this.dateRange.startDate}_${this.dateRange.endDate}.pdf`
        doc.save(fileName)
        ElMessage.success('PDF файл амжилттай татлаа')
      } catch (error) {
        console.error('Error exporting to PDF:', error)
        ElMessage.error('PDF файл татахад алдаа гарлаа')
      }
    },
    exportToCSV () {
      if (!this.tableData || this.tableData.length === 0) {
        ElMessage.warning('Татах өгөгдөл байхгүй байна')
        return
      }

      try {
        const headers = this.tableColumns.map(col => this.formatColumnName(col))
        const csvRows = [
          headers.join(','),
          ...this.tableData.map(row => this.tableColumns.map(col => {
            const value = row[col] || ''
            return `"${String(value).replace(/"/g, '""')}"`
          }).join(','))
        ]

        const csvContent = csvRows.join('\n')
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
        const fileName = `${this.reportName || 'Тайлан'}_${this.dateRange.startDate}_${this.dateRange.endDate}.csv`
        saveAs(blob, fileName)
        ElMessage.success('CSV файл амжилттай татлаа')
      } catch (error) {
        console.error('Error exporting to CSV:', error)
        ElMessage.error('CSV файл татахад алдаа гарлаа')
      }
    },
    printReport () {
      if (!this.tableData || this.tableData.length === 0) {
        ElMessage.warning('Хэвлэх өгөгдөл байхгүй байна')
        return
      }

      const printWindow = window.open('', '_blank')
      const columns = this.tableColumns.map(col => this.formatColumnName(col))
      const rows = this.tableData.map(row => this.tableColumns.map(col => row[col] || ''))

      const html = `
        <html>
          <head>
            <title>${this.reportName || 'Тайлан'}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              h1 { margin-bottom: 10px; }
              table { border-collapse: collapse; width: 100%; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; }
              @media print {
                body { padding: 0; }
                @page { margin: 1cm; }
              }
            </style>
          </head>
          <body>
            <h1>${this.reportName || 'Тайлан'}</h1>
            <p>Хугацаа: ${this.dateRange.startDate} - ${this.dateRange.endDate}</p>
            <table>
              <thead>
                <tr>
                  ${columns.map(col => `<th>${col}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${rows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('')}
              </tbody>
            </table>
          </body>
        </html>
      `

      printWindow.document.write(html)
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
      }, 250)
    },
    async loadBranches () {
      try {
        const response = await services.get('branchesWeb')
        if (response && response.type === 'success') {
          this.branches = response.data || []
        } else if (response && response.data) {
          this.branches = response.data || []
        }
      } catch (error) {
        console.error('Error loading branches:', error)
        this.branches = []
      }
    },
    getColumnFilters (column) {
      if (!this.tableData || this.tableData.length === 0) return []
      const uniqueValues = [...new Set(this.tableData.map(row => {
        const value = row[column]
        return value != null && value !== '' ? String(value) : null
      }).filter(v => v !== null))]

      return uniqueValues.sort().map(value => ({
        text: value,
        value: value
      }))
    },
    filterColumn (column, value, row) {
      if (!value || value.length === 0) return true
      const cellValue = row[column]
      if (cellValue === null || cellValue === undefined) return false
      return value.includes(String(cellValue))
    }
  }
}
</script>

<style lang="scss" scoped>
  .header_zone {
    background-color: white;
    padding: 20px;
  }

  .el-table {
    margin-bottom: 20px;
  }

  /* Report View Container */
  .report-view-container {
    padding: 0;
  }

  .report-title {
    font-size: 24px;
    margin: 0;
    word-break: break-word;
  }

  .header-actions-col {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .header-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
  }

  .action-btn {
    flex: 1;
    min-width: 80px;
  }

  /* Table Header Actions */
  .table-header-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  .header-actions-group {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  /* Table Filters */
  .table-filters {
    margin-bottom: 16px;
  }

  .table-search-input {
    width: 300px;
  }

  /* Date Range Header */
  .date-range-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  /* Query Preview */
  .query-preview {
    margin-bottom: 16px;
    padding: 16px;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
  }

  .query-code {
    background: #1f2937;
    color: #f9fafb;
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    font-family: 'Courier New', monospace;
    font-size: 13px;
    line-height: 1.6;
    margin: 0;
    white-space: pre-wrap;
    word-break: break-all;
  }

  /* Loading State */
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
  }

  .loading-text {
    margin-top: 16px;
    color: #6b7280;
    font-size: 16px;
  }

  /* Empty State */
  .empty-state {
    text-align: center;
    padding: 20px;
  }

  .empty-title {
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
    margin: 0 0 8px 0;
  }

  .empty-subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 20px 0;
  }

  .empty-action-button {
    margin-top: 12px;
  }

  /* Chart View Container */
  .chart-view-container {
    padding: 20px;
  }

  .chart-controls {
    margin-bottom: 24px;
  }

  .chart-wrapper {
    min-height: 400px;
    padding: 20px;
    background: #f9fafb;
    border-radius: 8px;
  }

  /* Mobile Table Scroll */
  @media (max-width: 768px) {
    .header_zone {
      padding: 12px;
    }

    .report-title {
      font-size: 20px;
      margin-bottom: 12px;
    }

    .header-actions-col {
      justify-content: flex-start;
    }

    .header-actions {
      width: 100%;
    }

    .action-btn {
      flex: 1 1 calc(50% - 4px);
      min-width: 0;
      font-size: 12px;
      padding: 8px 12px;
    }

    .el-table {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    .el-table :deep(.el-table__body-wrapper) {
      overflow-x: auto;
    }

    .el-card {
      padding: 12px;
    }

    .chart-view-container {
      padding: 12px;
    }

    .chart-controls {
      margin-bottom: 16px;
    }

    .chart-wrapper {
      min-height: 300px;
      padding: 12px;
    }
  }

  @media (max-width: 480px) {
    .header_zone {
      padding: 8px;
    }

    .report-title {
      font-size: 18px;
    }

    .action-btn {
      flex: 1 1 100%;
      font-size: 11px;
      padding: 6px 8px;
    }

    .chart-view-container {
      padding: 8px;
    }

    .chart-wrapper {
      min-height: 250px;
      padding: 8px;
    }
  }
</style>
